import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton'];
  optionalRefs = ['variantSelect', 'quantityInput', 'buyNowButton'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} - Tracks when a sticky add-to-cart click is awaiting cart result */
  #pendingStickyAdd = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    // Listen to direct input changes in sticky quantity field
    const stickyQtyInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[ref="quantityInput"]'));
    stickyQtyInput?.addEventListener('input', this.#handleQuantityInputChange, { signal });

    this.#getInitialQuantity();
    this.#updateStickyQuantityButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles variant/size selection change in sticky bar
   */
  /** @param {Event} event */
  handleVariantChange = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    const variantId = event.target.value;
    if (!variantId) return;

    // Find the main variant picker and trigger change
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    if (variantPicker) {
      const matches = Array.from(variantPicker.querySelectorAll(`[data-variant-id="${variantId}"]`));
      const target =
        matches.find((el) => {
          if (el instanceof HTMLInputElement) return !el.disabled;
          if (el instanceof HTMLOptionElement) return !el.disabled;
          return true;
        }) || matches[0];

      if (target instanceof HTMLInputElement) {
        target.checked = true;
        target.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (target instanceof HTMLOptionElement) {
        const select = target.closest('select');
        if (select) {
          // IMPORTANT: option.value is usually the option text value (e.g. "70 ml"),
          // not the variant id. We must set the select to the option's value.
          select.value = target.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } else {
        // Fallback: some pickers use the variant id as input/option value
        const legacyTarget = variantPicker.querySelector(`input[value="${variantId}"], option[value="${variantId}"]`);
        if (legacyTarget instanceof HTMLInputElement) {
          legacyTarget.checked = true;
          legacyTarget.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (legacyTarget instanceof HTMLOptionElement) {
          const select = legacyTarget.closest('select');
          if (select) {
            select.value = legacyTarget.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }

    // Update our dataset
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Increments the quantity in sticky bar
   */
  /** @param {Event} event */
  incrementQuantity = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const input = this.#getStickyQuantityInput();
    if (!input) return;

    const currentValue = parseInt(input.value) || 1;
    const max = parseInt(input.max) || Infinity;
    const step = parseInt(input.step) || 1;

    const nextValue = Math.min(max, currentValue + step);
    if (nextValue === currentValue) {
      this.#updateStickyQuantityButtonStates();
      return;
    }

    input.value = String(nextValue);
    this.#updateMainQuantity(nextValue);
    this.#updateStickyQuantityButtonStates();
  };

  /**
   * Decrements the quantity in sticky bar
   */
  /** @param {Event} event */
  decrementQuantity = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const input = this.#getStickyQuantityInput();
    if (!input) return;

    const currentValue = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const step = parseInt(input.step) || 1;

    const nextValue = Math.max(min, currentValue - step);
    if (nextValue === currentValue) {
      this.#updateStickyQuantityButtonStates();
      return;
    }

    input.value = String(nextValue);
    this.#updateMainQuantity(nextValue);
    this.#updateStickyQuantityButtonStates();
  };

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (!this.#targetAddToCartButton) return;

    // Mark that this cart add was initiated from the sticky bar
    this.#pendingStickyAdd = true;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
  };

  /**
   * Runs the sticky add-to-cart animation (button + fly-to-cart).
   * Only called after a successful cart add, not on errors.
   */
  #runStickyAddToCartAnimation() {
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    onAnimationEnd([this.refs.addToCartButton, flyToCartElement]).then(() => {
      this.#resetTimeout = setTimeout(() => {
        this.refs.addToCartButton.removeAttribute('data-added');
      }, 800);
    });
  }
  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();
    this.#updateStickyQuantityButtonStates();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    // If this wasn't initiated by the sticky bar, don't animate
    if (!this.#pendingStickyAdd) return;

    // If the event has a productId, ensure it matches this sticky bar's product
    const detail = event.detail || {};
    if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) {
      return;
    }

    const isSuccess = event.type === ThemeEvents.cartUpdate;
    if (isSuccess) {
      this.#runStickyAddToCartAnimation();
    }

    this.#pendingStickyAdd = false;
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Handles direct input changes in sticky quantity field
   * @param {Event} event - The input event
   */
  #handleQuantityInputChange = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    const newValue = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || Infinity;

    // Clamp value between min and max
    const clampedValue = Math.max(min, Math.min(max, newValue));
    
    if (clampedValue !== newValue) {
      input.value = String(clampedValue);
    }

    this.#updateMainQuantity(clampedValue);
    this.#updateStickyQuantityButtonStates();
  };

  /**
   * Safely gets the current sticky quantity input (works after morphing).
   * @returns {HTMLInputElement|null}
   */
  #getStickyQuantityInput() {
    // Prefer current DOM query (morph can replace elements)
    const fromDom = /** @type {HTMLInputElement|null} */ (
      this.refs.stickyBar?.querySelector('input[ref="quantityInput"]')
    );
    return fromDom || (/** @type {HTMLInputElement|null} */ (this.refs.quantityInput) ?? null);
  }

  /**
   * Disables/enables sticky +/- buttons based on min/max.
   * This prevents the UX of \"minus not working\" when user is at min quantity.
   */
  #updateStickyQuantityButtonStates() {
    const input = this.#getStickyQuantityInput();
    if (!input) return;

    const bar = this.refs.stickyBar;
    if (!bar) return;

    const minusBtn = /** @type {HTMLButtonElement|null} */ (bar.querySelector('.sticky-quantity-btn--minus'));
    const plusBtn = /** @type {HTMLButtonElement|null} */ (bar.querySelector('.sticky-quantity-btn--plus'));

    const value = parseInt(input.value) || 1;
    const min = parseInt(input.min) || 1;
    const max = parseInt(input.max) || Infinity;

    if (minusBtn) minusBtn.disabled = value <= min;
    if (plusBtn) plusBtn.disabled = value >= max;
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    // We no longer show quantity next to the CTA text.
    // Only keep the sticky quantity input synced with the current quantity.
    const input = this.#getStickyQuantityInput();
    if (input) input.value = this.#currentQuantity.toString();
  }

  /**
   * Updates the main form's quantity selector
   * @param {number} newQuantity - The new quantity value
   */
  #updateMainQuantity(newQuantity) {
    this.#currentQuantity = newQuantity;
    this.#updateButtonText();

    // Find and update the main quantity selector
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const quantitySelector = /** @type {any | null} */ (productForm.querySelector('quantity-selector-component'));
    if (quantitySelector?.setValue && quantitySelector?.onQuantityChange) {
      quantitySelector.setValue(String(newQuantity));
      quantitySelector.onQuantityChange();
      quantitySelector.updateButtonStates?.();
      return;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      productForm.querySelector('quantity-selector-component input[ref="quantityInput"], quantity-selector-component input[type="number"]')
    );
    if (!quantityInput) return;
    quantityInput.value = String(newQuantity);
    quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
